import React, { useState } from 'react';
import { Download, FileText, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';

/**
 * Facility Snapshot Export -- denormalized CSV download.
 *
 * One row per Active/Delinquent rental, joined inline with customer +
 * unit + unit_type so accountants / offshore staff / due-diligence
 * packets can sort + pivot without their own joins. Backend route:
 *   GET /api/facilities/{fid}/portfolio-export/current.csv
 *
 * Reached from the FACILITY tab tile since 2026-08-07 (owner: facility
 * data, not a document — briefly a FacilityForm tab on 08-06, corrected
 * to a tile the same night). Same /settings/portfolio-export route
 * throughout, so old bookmarks keep working.
 */

const PortfolioExport = () => {
  const { facilityId, currentFacility } = useFacility();
  const [downloading, setDownloading] = useState(false);
  const [fullDownloading, setFullDownloading] = useState(null); // fmt or null
  const [error, setError] = useState(null);
  const [lastDownload, setLastDownload] = useState(null);

  const downloadFull = async (fmt) => {
    if (!facilityId) return;
    setFullDownloading(fmt);
    setError(null);
    try {
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/portfolio-export/full?format=${fmt}`,
        { responseType: 'blob' },
      );
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `full-export.${fmt === 'csv' ? 'zip' : fmt}`;
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setLastDownload({ filename, at: new Date().toLocaleString() });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Export failed');
    } finally {
      setFullDownloading(null);
    }
  };

  const download = async () => {
    if (!facilityId) return;
    setDownloading(true);
    setError(null);
    try {
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/portfolio-export/current.csv`,
        { responseType: 'blob' },
      );

      // Filename comes from Content-Disposition header so it matches the
      // server-side format (portfolio_<facility-slug>_<iso-date>.csv).
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `portfolio_${facilityId}.csv`;

      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      setLastDownload({
        filename,
        at: new Date().toLocaleString(),
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <FileText className="h-5 w-5 text-slate-400" />
          Facility Export
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Download current customers, rentals, and units as a single denormalized CSV
          {currentFacility?.name && <> &middot; {currentFacility.name}</>}
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 max-w-2xl">
        <h2 className="text-[15px] font-semibold text-slate-900 mb-2">Current facility snapshot</h2>
        <p className="text-[13px] text-slate-600 mb-4 leading-relaxed">
          One row per Active or Delinquent rental, denormalized with the renter,
          unit, rate, balance, gate code, status, and start date. Use it for
          accountant handoffs, investor due-diligence packets, or quick sort
          + pivot operations in Excel / Google Sheets.
        </p>

        <details className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <summary className="text-[12px] font-medium text-slate-700 cursor-pointer">
            Columns included (22)
          </summary>
          <ul className="mt-2 text-[12px] text-slate-600 grid grid-cols-2 gap-x-4 gap-y-0.5 list-disc list-inside">
            <li>customer_number</li>
            <li>customer_name</li>
            <li>customer_email</li>
            <li>customer_phone</li>
            <li>unit_number</li>
            <li>unit_type</li>
            <li>unit_size</li>
            <li>monthly_rate</li>
            <li>balance</li>
            <li>rental_status</li>
            <li>start_date</li>
            <li>next_bill_date</li>
            <li>gate_code</li>
            <li>autopay_enabled</li>
            <li>tenant_protection_plan</li>
            <li>customer_address / city / state / zip</li>
            <li>rental_id / customer_id / unit_id</li>
          </ul>
        </details>

        {error && (
          <div className="mb-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {error}
          </div>
        )}
        {lastDownload && !error && (
          <div className="mb-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-700 flex items-start gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 mt-0.5" />
            Downloaded <span className="font-mono">{lastDownload.filename}</span> at {lastDownload.at}
          </div>
        )}

        <button
          type="button"
          onClick={download}
          disabled={downloading || !facilityId}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium"
        >
          {downloading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Download className="h-3.5 w-3.5" />}
          {downloading ? 'Generating...' : 'Download CSV'}
        </button>

        <p className="text-[11px] text-slate-400 mt-3">
          Only Active and Delinquent rentals are included. Ended rentals are excluded
          (they belong in a historical archive, not a current snapshot).
        </p>
      </div>

      {/* FULL data export — the data-portability guarantee. */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 max-w-2xl mt-5">
        <h2 className="text-[15px] font-semibold text-slate-900 mb-2">Full data export</h2>
        <p className="text-[13px] text-slate-600 mb-4 leading-relaxed">
          Everything, one click: every operational record for this facility —
          customers, units, rentals, invoices, payments, leases, leads, notes,
          notifications, workflows, fees, and more. Your data is yours;
          credentials and secrets are never included.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {[
            { fmt: 'json', label: 'JSON (single file)' },
            { fmt: 'csv', label: 'CSV (ZIP, one file per table)' },
            { fmt: 'sql', label: 'SQL (importable dump)' },
          ].map(({ fmt, label }) => (
            <button
              key={fmt}
              type="button"
              onClick={() => downloadFull(fmt)}
              disabled={fullDownloading !== null || !facilityId}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium"
            >
              {fullDownloading === fmt
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Download className="h-3.5 w-3.5" />}
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioExport;
